import { useEffect, useRef, useState } from 'react';
import Button from '../../../components/button/Button'
import { CSSTransition } from 'react-transition-group';
import RateSlider from '../../../components/ui/rate-slider';
import { getDisplayPrice } from '../../../utilities/priceHelper';
import { useGetProposalByIdQuery } from '../../proposals/proposalsApi';
import { useParams } from 'react-router-dom';
import { t } from '../../../utilities/translator';
import { pushEvent } from '../../../utilities/analytics';

type Props = {
  offer: any;
  onSave: ({ proposalId, offerId, commission, muid }: { proposalId: number; offerId: string; commission: number | null; muid: string }, newTotalPrice: number) => void;
}

const OfferCommissionControl = ({ offer, onSave }: Props) => {
  const lsUser = localStorage.getItem('hf_user');
  const user = lsUser ? JSON.parse(lsUser) : null;

  const [isOpen, setIsOpen] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { data: proposal } = useGetProposalByIdQuery({
    proposalId: Number(id),
    muid: user?.user?.muid || '',
  });

  const proposalOffer = proposal?.offers?.find(proposalOffer => offer.hfOfferId === proposalOffer.offerId);
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const agentCommission = user.userContextMetaData?.b2bDisplayProperties?.commission?.value || 0;
  const proposalCommission = proposal?.commission || agentCommission;
  const defaultCommission = proposalOffer?.proposal_offer_commission_value || proposalCommission || agentCommission;
  const [commission, setCommission] = useState(defaultCommission);
  const basePrice = Math.ceil(offer.price * (1 - ((defaultCommission) / 100)));
  const currentCommission = Math.ceil(basePrice / (1 - (defaultCommission / 100)) - basePrice);
  const defaultProposalCommissionPrice = Math.ceil(basePrice / (1 - (proposalCommission / 100)) - basePrice);
  const newCommissionPrice =  Math.ceil((basePrice / (1 - (commission / 100))) - basePrice);
  const newTotalPrice = basePrice + newCommissionPrice;
  const sliderLabel = user.userContextMetaData?.b2bDisplayProperties?.commission?.display ? `${commission}%` : getDisplayPrice(newTotalPrice);

  useEffect(() => {
    if (!proposalOffer?.proposal_offer_commission_value && proposal?.commission) {
      setCommission(proposal?.commission);
    }
  }, [proposal?.commission, proposalOffer?.proposal_offer_commission_value])

  const onOpenPopover = () => {
    setIsOpen(true);
    pushEvent('proposals_adjustOfferCommissionModal_show');
  }

  const onFinalCommissionChange = () => {
    pushEvent('proposals_adjustOfferCommissionModal_commissionChange')
  }

  const handleReset = () => {
    setCommission(proposalCommission);
    onSave({
      proposalId: proposal?.id || 0,
      offerId: offer.hfOfferId,
      commission: null,
      muid: user?.user?.muid || '',
    }, basePrice + defaultProposalCommissionPrice)
    pushEvent('proposals_adjustOfferCommission_commissionReset', {
      agentCommission: user?.userContextMetaData?.b2bDisplayProperties?.commission?.value,
      commission: proposalCommission,
    });
  }

  const handleSave = () => {
    setIsOpen(false);
    onSave({
      proposalId: proposal?.id || 0,
      offerId: offer.hfOfferId,
      commission,
      muid: user?.user?.muid || '',
    }, newTotalPrice)
    pushEvent('proposals_adjustOfferCommissionModal_commissionSave', {
      agentCommission: user?.userContextMetaData?.b2bDisplayProperties?.commission?.value,
      commission,
    });
  }

  return (
    <div className="js_adjust-commission">
      <div className="flex gap-2">
        <Button
          label={<i className="ri-edit-line text-lg leading-none" />}
          onClick={onOpenPopover}
          className="edit-commission-btn shrink-0"
          type="secondary"
          size="sm"
        />
        {!!proposalOffer?.proposal_offer_commission_value && (
          <Button
            label={<i className="ri-loop-left-line text-lg leading-none" />}
            onClick={handleReset}
            className="edit-commission-btn shrink-0"
            type="secondary"
            size="sm"
          />
        )}
      </div>

      <CSSTransition
        in={isOpen}
        classNames="card-popover-animate"
        timeout={200}
        unmountOnExit
        mountOnEnter
        nodeRef={modalOverlayRef}
      >
        <div ref={modalOverlayRef} className="adjust-commission-panel">
          <div className="adjust-commission-panel-content">
            <div className="flex justify-end">
              <button className="close-btn" onClick={() => setIsOpen(false)}><i className="ri-close-line text-xl leading-none" /></button>
            </div>
            <div className="text font-semibold text-center">{t('proposals.adjustOfferCommission')}</div>
            <RateSlider
              label={sliderLabel}
              min={user.userContextMetaData?.b2bDisplayProperties?.commission?.min || 10}
              max={user.userContextMetaData?.b2bDisplayProperties?.commission?.max || 30}
              values={[commission]}
              onChange={(value) => setCommission(value[0])}
              onFinalChange={onFinalCommissionChange}
            />
            <div className="flex flex-col gap-3">

              {user.userContextMetaData?.b2bDisplayProperties?.commission?.display && (
                <>
                <div className="flex justify-between text-tertiary">
                  <div className="font-medium">{t('proposals.currentCommission')}</div>
                  <div className="font-semibold">{getDisplayPrice(currentCommission)}</div>
                </div>
                <div className="flex justify-between">
                  <div className="font-medium">{t('proposals.newCommission')}</div>
                  <div className="font-semibold">{getDisplayPrice(newCommissionPrice)}</div>
                </div>
                <div className="flex justify-between commission-total-row">
                  <div className="font-medium">{t('proposals.newTotalPrice')}</div>
                  <div className="font-semibold">{getDisplayPrice(newTotalPrice)}</div>
                </div>
                </>
              )}
              {!user.userContextMetaData?.b2bDisplayProperties?.commission?.display && (
                <>
                  <div className="flex justify-between text-tertiary text-[14px]">
                    <div className="font-medium">{t('proposals.defaultPrice')}</div>
                    <div className="font-semibold">{getDisplayPrice(basePrice + defaultProposalCommissionPrice)}</div>
                  </div>
                  <div className="flex justify-between text-tertiary text-[14px]">
                    <div className="font-medium">{t('proposals.priceDifference')}</div>
                    <div className="font-semibold">{getDisplayPrice(newCommissionPrice - defaultProposalCommissionPrice)}</div>
                  </div>
                </>
              )}

              <div className="flex justify-between mt-6">
                <Button label={t('buttons.cancel')} type="secondary" size="sm" onClick={() => setIsOpen(false)} />
                <Button label={t('buttons.Save')} opticalStart endIcon={<i className="ri-check-line text-lg leading-none" />} type="primary" size="sm" onClick={handleSave} />
              </div>
            </div>
          </div>
        </div>
      </CSSTransition>
    </div>
  )
}

export default OfferCommissionControl
